import {
    randWord,
    randNumber,
    randEmail,
    randBoolean,
    randUrl,
    randFirstName,
    randLastName,
    randPhoneNumber,
} from "@ngneat/falso";
import { RequestConfig, RequestOptions } from "../api";
import {
    User,
    Identifiable,
    Role,
    DecodedAccessToken,
    Tenant,
    UpdateProfilePasswordRequest,
    ImpersonationResponse,
    CreatePhotoRequest,
    Photo,
    AwsFile,
    AppSettings,
    QueryResult,
    MutationResult,
    DeleteMutation,
    PageParams,
    SortOption,
    SortParams,
    UpdateMutation,
    LoginResponse,
} from "../models";

export const Any = {
    accessToken: (accessToken?: Partial<DecodedAccessToken>): DecodedAccessToken => {
        return {
            provider: accessToken?.provider ?? randWord(),
            user_id: accessToken?.user_id ?? randWord(),
            tenant_id: accessToken?.tenant_id ?? randWord(),
            given_name: accessToken?.given_name ?? randWord(),
            family_name: accessToken?.family_name ?? randWord(),
            email: accessToken?.email ?? randWord(),
            qr_code_image: accessToken?.qr_code_image ?? randWord(),
            manual_entry_code: accessToken?.manual_entry_code ?? randWord(),
            nbf: accessToken?.nbf ?? randNumber(),
            exp: accessToken?.exp ?? randNumber(),
            auth_time: accessToken?.auth_time ?? randNumber(),
        };
    },
    appSettings: (partial?: Partial<AppSettings>): AppSettings => {
        return {
            id: randNumber(),
            tenantId: randNumber(),
            ...partial,
        };
    },
    arrayOf: function <T>(generator: () => T, min: number = 3, max: number = 10): T[] {
        const count = Math.max(min, randNumber({ max }));
        const returnValue = [];
        for (let i = 0; i < count; i++) {
            returnValue.push(generator());
        }
        return returnValue;
    },
    awsFile: (partial?: Partial<AwsFile>): AwsFile => {
        return {
            filename: randWord(),
            content: randWord(),
            url: randWord(),
            ...partial,
        };
    },
    updatePassword: (update?: Partial<UpdateProfilePasswordRequest>): UpdateProfilePasswordRequest => {
        return {
            emailAddress: "",
            currentPassword: "",
            newPassword: "",
            newPasswordConfirm: "",
            ...update,
        };
    },
    config: (partial?: Partial<RequestConfig>): RequestConfig => {
        return {
            headers: {
                "Content-Type": "application/json",
            },
            ...partial,
        };
    },
    createPhotoRequest: (partial?: Partial<CreatePhotoRequest>): CreatePhotoRequest => {
        return {
            content: randWord(),
            ...partial,
        };
    },
    deleteMutation: (): DeleteMutation => {
        return {
            mutate(variables) {},
            mutateAsync: (variables) => Promise.resolve(true),
            isError: false,
            isLoading: false,
            isSuccess: false,
        };
    },
    identifiable: function (identifiable?: Partial<Identifiable>): Identifiable {
        return {
            id: identifiable?.id ?? randNumber(),
        };
    },
    impersonationResponse: (partial?: Partial<ImpersonationResponse>): ImpersonationResponse => {
        return {
            userId: randNumber(),
            emailAddress: randEmail(),
            impersonatorId: randNumber(),
            statusCode: randNumber(),
            success: randBoolean(),
            ...partial,
        };
    },
    loginResponse: (partial?: Partial<LoginResponse>): LoginResponse => {
        return {
            email: randEmail(),
            userId: randNumber(),
            subjectId: randWord(),
            mfaEnabled: randBoolean(),
            mfaSetupComplete: randBoolean(),
            mfaOptionId: randNumber(),
            tenantId: randNumber(),
            authenticatorQRCode: {
                qrCodeImageUrl: randUrl(),
                manualEntryCode: randWord(),
            },
            ...partial,
        };
    },
    mutationResult: <TData, TVariables = Partial<TData>>(data?: TData): MutationResult<TData, unknown, TVariables> => {
        return {
            mutate: (variables) => {},
            mutateAsync: (variables) => Promise.resolve<TData>(data ?? ({} as TData)),
            isError: false,
            isLoading: false,
            isSuccess: false,
        };
    },
    numberExcept: (number: number) => {
        let num = randNumber();
        while (num === number) {
            num = randNumber();
        }
        return num;
    },
    object: <T>(partial?: Partial<T>): T => {
        return {
            ...partial,
        } as T;
    },
    pageParams: (): PageParams => {
        return {
            page: randNumber(),
            pageSize: randNumber(),
        };
    },
    params: (): object => {
        return { isActive: randBoolean() };
    },
    partialAppSettings: (): Partial<AppSettings> => {
        return {
            tenantId: randNumber(),
        };
    },
    partialUpdatePassword: (): Partial<UpdateProfilePasswordRequest> => {
        return {
            emailAddress: randEmail(),
        };
    },
    partialCreatePhotoRequest: (): Partial<CreatePhotoRequest> => {
        return {
            content: randWord(),
        };
    },
    partialTenant: (): Partial<Tenant> => {
        return {
            name: randWord(),
        };
    },
    partialUser: (): Partial<User> => {
        return {
            firstName: randFirstName(),
        };
    },
    photo: (partial?: Partial<Photo>): Photo => {
        return {
            id: randNumber(),
            path: randUrl(),
            ...partial,
        };
    },
    queryResult: <T>(queryResult?: Partial<QueryResult<T>>): QueryResult<T> => {
        return {
            data: undefined,
            error: null,
            isError: false,
            isLoading: true,
            isSuccess: false,
            isFetching: false,
            ...queryResult,
        };
    },
    requestOptions: <T = unknown>(partial?: Partial<RequestOptions<T>>): RequestOptions<T> => {
        return {
            url: randUrl(),
            ...partial,
        };
    },
    role: function (role?: Partial<Role>): Role {
        return {
            name: role?.name ?? randWord(),
            id: role?.id ?? randNumber(),
            isActive: role?.isActive ?? randBoolean(),
            permissions: role?.permissions ?? [],
        };
    },
    sortOption: function (sortOption?: Partial<SortOption>): SortOption {
        return {
            value: sortOption?.value ?? randWord(),
            label: sortOption?.label ?? randWord(),
            orderDesc: sortOption?.orderDesc ?? (randBoolean().toString() as "true" | "false"),
        };
    },
    sortParams: (): SortParams => {
        return {
            orderBy: randWord(),
            orderDesc: randBoolean().toString(),
        };
    },
    tenant: function (tenant?: Partial<Tenant>): Tenant {
        return {
            name: tenant?.name ?? randWord(),
            logoUri: tenant?.logoUri ?? randWord(),
            id: tenant?.id ?? randNumber(),
            isActive: tenant?.isActive ?? randBoolean(),
            parentTenantId: tenant?.parentTenantId,
        };
    },
    updateMutation: <TData>(data?: TData): UpdateMutation<TData> => {
        return {
            mutate(variables) {},
            mutateAsync: (variables) => Promise.resolve<TData>(data ?? ({} as TData)),
            isError: false,
            isLoading: false,
            isSuccess: false,
        };
    },
    user: function (user?: Partial<User>): User {
        return {
            id: user?.id ?? randNumber(),
            firstName: user?.firstName ?? randFirstName(),
            lastName: user?.lastName ?? randLastName(),
            emailAddress: user?.emailAddress ?? randEmail(),
            phoneNumber: user?.phoneNumber ?? randPhoneNumber(),
            isActive: user?.isActive ?? randBoolean(),
            mfaOptionId: user?.mfaOptionId ?? randNumber(),
            mfaEnabled: user?.mfaEnabled ?? randBoolean(),
            mfaSetupComplete: user?.mfaSetupComplete ?? randBoolean(),
        };
    },
    voidFn: () => {},
    voidPromise: () => Promise.resolve(),
};
